import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const {FiTool, FiShield, FiAlertTriangle, FiCheck, FiX, FiTruck, FiHardHat, FiSettings} = FiIcons;

const ToolsEquipmentPage = () => {
  const threats = [
    {
      title: "Equipment damage",
      description: "An essential piece of heavy machinery gives out — days after the warranty expires.",
      icon: FiSettings
    },
    {
      title: "Stolen tools",
      description: "A trailer of gear or equipment on a job site goes missing — and needs replacing — fast.",
      icon: FiTruck
    },
    {
      title: "The risk of borrowed gear",
      description: "Another contractor loans you an expensive power tool that breaks and needs replacing.",
      icon: FiTool
    }
  ];

  const industries = [
    {
      name: "Construction",
      description: "Power tools, heavy machinery, and specialized construction equipment protection."
    },
    {
      name: "Cleaning Services",
      description: "Commercial cleaning equipment, vacuum systems, and janitorial tools coverage."
    },
    {
      name: "Landscaping",
      description: "Mowers, trimmers, blowers, and outdoor maintenance equipment protection."
    },
    {
      name: "Plumbing",
      description: "Pipe cutters, drain snakes, leak detection equipment, and specialty tools."
    },
    {
      name: "HVAC",
      description: "Diagnostic equipment, refrigerant recovery units, and climate control tools."
    },
    {
      name: "Electrical",
      description: "Testing equipment, wire pulling systems, and electrical installation tools."
    }
  ];

  const coveredItems = [
    "Sanding power tools",
    "Saws and cutting equipment",
    "Laser levels",
    "Dust-control systems",
    "Ladders and workbenches",
    "Power drills",
    "Blowtorches",
    "CNC routers",
    "Oscillating multi-tools",
    "Concrete moisture meter",
    "Stand lights",
    "Hearing protection",
    "Helmets and safety gear",
    "Power tubing cutters"
  ];

  const notCovered = [
    "Corrosion, rust or wear and tear",
    "Equipment you rent from someone else",
    "Costs beyond the limit of your policy",
    "Normal maintenance and upkeep"
  ];

  const coverageScenarios = [
    {
      title: "Theft from trailer",
      description: "Someone breaks in while you're in transit to a job and takes all of your tools.",
      covered: true
    },
    {
      title: "Job site theft",
      description: "A generator and half of your specialty tools go missing while you're on the job.",
      covered: true
    },
    {
      title: "Accidental damage",
      description: "Your employee accidentally punctures your air compressor, and you'll need a replacement.",
      covered: true
    },
    {
      title: "Borrowed equipment damage",
      description: "Equipment borrowed from someone else breaks and needs replacing (T&E Select Pro or Pro Plus).",
      covered: true
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80)'}}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Tools & Equipment Insurance: Protection for Your Essential Gear
            </motion.h1>
            <motion.p
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Protect the power tools and equipment you bring to job sites. Coverage starts at just $19/month when added to general liability.
            </motion.p>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
            >
              <CTAButton 
                text="Get Your Tools & Equipment Quote" 
                size="xl" 
                className="mb-8"
              />
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.6}}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Selection */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Choose Your Primary Industry to Get Started
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="text-center p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiHardHat} className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Construction</h3>
              <CTAButton text="Get a Quote" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="text-center p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiTool} className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cleaning</h3>
              <CTAButton text="Get a Quote" className="w-full" />
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-purple-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiSettings} className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Other Industries</h3>
              <CTAButton text="Get a Quote" className="w-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Threats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The Power Tools or Equipment You Bring to Work Need Protection From:
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {threats.map((threat, index) => (
              <motion.div
                key={threat.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.2}}
                className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={threat.icon} className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{threat.title}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="text-center mt-12"
          >
            <CTAButton text="Get Instant Quote" />
          </motion.div>
        </div>
      </section>

      {/* What is Tools & Equipment Insurance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Tools & Equipment Insurance?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Your tools and equipment are essential to run your business when they're in your vehicle, stored in an offsite trailer or shed, or at a job site.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Tool and equipment coverage could help get you back to work quickly and help with replacement or repairs if they're stolen, lost or damaged.
              </p>
              <p className="text-lg text-gray-700">
                We offer it as an add-on to NEXT general liability insurance for contractors and cleaning businesses.
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Tool Insurance Could Help If:</h3>
              <div className="space-y-4">
                {coverageScenarios.map((scenario, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{scenario.title}</h4>
                      <p className="text-gray-700 text-sm">{scenario.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Needs Equipment Insurance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Who Needs Equipment Insurance?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tool and equipment insurance is a smart choice for anyone who brings their own equipment to different locations to get a job done.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <SafeIcon icon={FiTool} className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{industry.name}</h3>
                <p className="text-gray-600 text-sm">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Can Insurance for Tools and Equipment Cover?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* What's Covered */}
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Covered</h3>
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600" />
                    <span className="text-gray-700">Employee tools and equipment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600" />
                    <span className="text-gray-700">Essential work clothing and safety gear</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600" />
                    <span className="text-gray-700">Borrowed equipment (with Pro coverage)</span>
                  </li>
                </ul>
                
                <h4 className="font-semibold text-gray-900 mb-3">Examples of covered items:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {coveredItems.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-3 w-3 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* What's Not Covered */}
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Not Covered</h3>
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <p className="text-gray-700 mb-4">
                  Tool and equipment insurance covers a lot if your tools go missing — but it won't cover everything.
                </p>
                <ul className="space-y-3">
                  {notCovered.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <SafeIcon icon={FiX} className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Be sure to check your policy for everything that will and won't be covered, including specific limits and deductibles.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How Much Does Tools & Equipment Insurance Cost?
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <div className="bg-primary-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Affordable Add-On Coverage</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-primary-600 text-white rounded-full p-2">
                      <SafeIcon icon={FiShield} className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Starting at $19/month</p>
                      <p className="text-gray-600 text-sm">When added to general liability</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-secondary-600 text-white rounded-full p-2">
                      <SafeIcon icon={FiCheck} className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Save up to 10%</p>
                      <p className="text-gray-600 text-sm">When bundling multiple policies</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Your cost is determined by:</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">The type of work you do</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Number of employees</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Your claims history</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Your work experience</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">The limits you choose</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">The state where you work</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Get Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How Do You Get Tool and Equipment Insurance at NEXT?
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-gradient-to-br from-primary-50 to-blue-50 p-8 rounded-lg border border-primary-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    1
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Start Free Quote</h3>
                  <p className="text-gray-600 text-sm">Answer a few questions about your business</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    2
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Choose Coverage</h3>
                  <p className="text-gray-600 text-sm">See options and adjust limits as needed</p>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    3
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Buy Online</h3>
                  <p className="text-gray-600 text-sm">Get insured immediately after payment</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg text-gray-700 mb-6">
                  You can buy tools and equipment insurance as an add-on to your general liability coverage 100% online in about 10 minutes. You're insured immediately after payment, and you'll have access to your certificate of insurance at no extra cost.
                </p>
                <CTAButton text="Start a Free Quote with NEXT Today" size="lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Protect Your Essential Tools and Equipment Today
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-xl mb-8"
          >
            Don't let stolen or damaged tools shut down your business. Get comprehensive tools and equipment coverage starting at just $19/month.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            <CTAButton 
              text="Get Your Tools & Equipment Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ToolsEquipmentPage;